import { FRAME_RIM_TYPES } from './enums.js';
/**
 * `CAT-002` and `CAT-003`: what makes a frame variant itself, and what a rimless one owes the lab.
 *
 * Both were vocabulary without behaviour. `frame_variant` carried `eye_size_mm` and keyed on model
 * and colour alone, so `CAT-002`'s "model, colour and size" was two thirds true. And the only
 * mention of `rimless` in the whole repository was in Mira's prompt, asking it to read `rim_type`
 * off a photo, with nothing consuming the answer. `later_todo.md` 63.
 */
/**
 * The rim types that need mounting data. `CAT-003`.
 *
 * A full-rim frame holds the lens in a groove the frame already has, so there is nothing to tell a
 * lab. A rimless frame is drilled and a semi-rimless one is grooved and nylon-corded, and in both
 * cases the lab is cutting a lens to fit hardware it cannot see.
 *
 * **Derived from `FRAME_RIM_TYPES` rather than written out again**, which is the fix for a defect
 * this set caused. It used to be a second literal, and `tools/seed` wrote `half_rim` for its
 * semi-rimless frame: two vocabularies for one thing, so `requiresMountingData` answered false and
 * `assertSendableToLab` let a semi-rimless frame go to a lab with no drill or groove data. The
 * registry is now the only spelling, and this list is the complement of the one rim type that needs
 * nothing, so a fourth rim type added later is treated as needing data until somebody says
 * otherwise. That is the safe direction: the cost of asking for mount data a frame does not need is
 * a question, and the cost of not asking is a ruined lens.
 */
const NEEDS_MOUNTING = new Set(FRAME_RIM_TYPES.filter((rim) => rim !== 'full_rim'));
export function requiresMountingData(rimType) {
    return rimType !== null && rimType !== undefined && NEEDS_MOUNTING.has(rimType);
}
export class FrameError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'FrameError';
    }
}
/**
 * Whether this frame can be sent to a lab. `CAT-003`.
 *
 * **Checked here rather than at the catalogue write.** A rimless frame typed in before anybody
 * knows its drill notes is a catalogue somebody is still filling in, and refusing that row would
 * make them invent the notes to get past it. The moment missing drill data actually costs
 * something is the moment a job goes to a lab, because that is when a lens gets cut to fit
 * hardware the lab cannot see.
 */
export function assertSendableToLab(frame) {
    if (!requiresMountingData(frame.rimType))
        return;
    const notes = frame.mountingNotes ?? '';
    if (notes.trim() === '') {
        throw new FrameError('MOUNTING_DATA_REQUIRED', 'A rimless or semi-rimless frame needs its drill, notch or groove notes before it goes to a ' +
            'lab (CAT-003). The lab is cutting a lens to fit hardware it cannot see.');
    }
}
/**
 * `CAT-002`: a variant is model, colour and size, so this is what makes two of them the same one.
 *
 * Exported because the key exists in two places that have to agree: the unique constraint on
 * `frame_variant`, and whatever decides a typed-in frame is one the shop already has. §13 records
 * that frames arrive in Pakistan without barcodes and the same model gets entered repeatedly, so
 * the second is not hypothetical.
 */
export function frameVariantKey(variant) {
    return [
        variant.frameModelId,
        variant.colourCode.trim().toUpperCase(),
        variant.eyeSizeMm ?? '',
        variant.bridgeMm ?? '',
    ].join('|');
}
//# sourceMappingURL=frames.js.map